// Login page component
import React, { useState, useEffect } from 'react';
import { Alert } from '@chakra-ui/react';
import { useColorModeValue } from '../hooks/useColorModeValue';
import { FiAlertCircle } from 'react-icons/fi';
import {
  Box,
  Button,
  Container,
  Field,
  Input,
  VStack,
  Heading,
  Text,
  Link,
  Card,
  Separator,
  HStack,
  Image,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { LoginCredentials } from '../types';
import { FcGoogle } from 'react-icons/fc';

// Google OAuth configuration
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || 'your-google-client-id';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, options: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

const LoginPage: React.FC = () => {
  const [error, setError] = useState<string>('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const { t } = useTranslation();

  const schema = yup.object({
    email: yup.string().email(t('auth.invalidEmail')).required(t('auth.emailRequired')),
    password: yup.string().required(t('auth.passwordRequired')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    resolver: yupResolver(schema),
  });

  // Initialize Google OAuth
  useEffect(() => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleLogin,
      });
    }
  }, []);

  const handleGoogleLogin = async (response: any) => {
    try {
      setGoogleLoading(true);
      setError('');
      
      // Use the useAuth hook for Google login
      await loginWithGoogle(response.credential);
      navigate('/dashboard');
    } catch (err: any) {
      setError(t('auth.googleLoginFailed'));
      console.error('Google login error:', err);
    } finally {
      setGoogleLoading(false);
    }
  };

  const onSubmit = async (data: LoginCredentials) => {
    setEmailLoading(true);
    try {
      setError('');
      await login(data);
      navigate('/dashboard');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (detail && typeof detail === 'object') {
        setError(detail.message || detail.error || t('auth.loginFailed'));
      } else {
        setError(t('auth.loginFailed'));
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const handleGoogleButtonClick = () => {
    if (window.google) {
      window.google.accounts.id.prompt();
    } else {
      setError(t('auth.googleLoginUnavailable'));
    }
  };

  return (
    <Box bg={bg} minH="100vh" py={12}>
      <Container maxW="md">
        <Card.Root bg={cardBg} shadow="lg">
          <Card.Body>
            <VStack gap={6}>
              <VStack gap={2} align="center">
                <Image src="/neurev-logo.png" alt={t('auth.neurevLogoAlt')} height="80px" objectFit="contain" />
                <Heading size="lg" textAlign="center">
                  {t('auth.signInTitle')}
                </Heading>
                <Text textAlign="center" color="gray.600">
                  {t('auth.welcomeBack')}
                </Text>
              </VStack>

              {error && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator>
                    <FiAlertCircle />
                  </Alert.Indicator>
                  <Alert.Description>{error}</Alert.Description>
                </Alert.Root>
              )}

              {/* Google Login Button */}
              <Box w="full">
                <Button
                  w="full"
                  size="lg"
                  variant="outline"
                  onClick={handleGoogleButtonClick}
                  loading={googleLoading}
                  loadingText={t('auth.signingInWithGoogle')}
                  _hover={{
                    bg: 'gray.50',
                    borderColor: 'gray.300',
                  }}
                >
                  <FcGoogle size={20} /> {t('auth.continueWithGoogle')}
                </Button>
              </Box>

              {/* Divider */}
              <HStack w="full" gap={4}>
                <Separator />
                <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                  {t('auth.or')}
                </Text>
                <Separator />
              </HStack>

              {/* Email/Password Login Form */}
              <Box w="full">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <VStack gap={4}>
                    <Field.Root invalid={!!errors.email}>
                      <Field.Label>{t('auth.emailAddress')}</Field.Label>
                      <Input
                        type="email"
                        placeholder={t('auth.enterYourEmail')}
                        {...register('email')}
                      />
                      {errors.email && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.email.message}
                        </Text>
                      )}
                    </Field.Root>

                    <Field.Root invalid={!!errors.password}>
                      <Field.Label>{t('auth.password')}</Field.Label>
                      <Input
                        type="password"
                        placeholder={t('auth.enterYourPassword')}
                        {...register('password')}
                      />
                      {errors.password && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.password.message}
                        </Text>
                      )}
                    </Field.Root>

                    <Button
                      type="submit"
                      colorScheme="brand"
                      size="lg"
                      w="full"
                      loading={emailLoading}
                      loadingText={t('auth.signingIn')}
                    >
                      {t('auth.signInWithEmail')}
                    </Button>
                  </VStack>
                </form>
              </Box>

              <Text textAlign="center">
                {t('auth.noAccount')}{' '}
                <Link {...{as: RouterLink, to: "/register"} as any} color="brand.600">
                  {t('auth.signUpHere')}
                </Link>
              </Text>
            </VStack>
          </Card.Body>
        </Card.Root>
      </Container>
    </Box>
  );
};

export default LoginPage;
